import * as THREE from 'three/webgpu';
import { PointsMaterialFactory } from '../../../../material/PointsMaterialFactory';
import type { SketchGroup } from '../../../../mesh/sketch/SketchGroup';
import { SketchPointKind } from '../../../../mesh/sketch/ISketchObject';
import { SketchPointObject } from '../../../../mesh/sketch/SketchPointObject';
import {
	SketchSplineObject,
} from '../../../../mesh/sketch/SketchSplineObject';
import type { ProjectionResult, WorldCoordinate } from '../../model/ProjectionResult';
import type {
	SplineReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/SplineReferenceBuilder';
import { SketchReferenceInjectedEndpointParentScope } from '../SketchReferenceInjectedEndpointParentScope';

export type {
	SplineReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/SplineReferenceBuilder';

/**
 * 普通样条引用构建器。
 * 说明：
 * 1. Convert Entities 创建的是新的目标样条对象，而不是复用来源对象；
 * 2. 控制点按投影载荷重建，采样点直接复用投影结果，保证同步创建不依赖 Worker；
 * 3. 样条控制点由 `SketchSplineObject` 作为子节点管理，factory 只需挂载根对象。
 */
export class SplineReferenceBuilder {
	/**
	 * 构建普通样条对象。
	 *
	 * @param input 构建输入。
	 * @returns 已完成几何初始化的普通样条对象。
	 */
	public build(input: SplineReferenceBuilderInput): SketchSplineObject {
		const controlPointWorlds = input.geometryPayload.controlPointWorlds;
		const firstControlPointWorld = controlPointWorlds[0];
		const lastControlPointWorld = controlPointWorlds[controlPointWorlds.length - 1];
		if (!firstControlPointWorld || !lastControlPointWorld || controlPointWorlds.length < 2) {
			throw new Error('普通样条引用构建失败：控制点数量不足。');
		}
		const startPoint = input.startPoint
			?? createControlPoint(firstControlPointWorld, input.targetSketch);
		/**
		 * 与绘制链路一致，起/终存取位保存首末编辑控制点；周期闭合由曲线定义表达。
		 * 将末点替换成首点会丢失最后一个有效控制点，导致引用重建及轮廓读取改变曲线。
		 */
		const endPoint = input.endPoint ?? createControlPoint(lastControlPointWorld, input.targetSketch);
		const interiorControlPoints = controlPointWorlds
			.slice(1, -1)
			.map(position => createControlPoint(position, input.targetSketch));

		const injectedEndpointParentScope = new SketchReferenceInjectedEndpointParentScope([
			input.startPoint,
			input.endPoint,
		]);
		try {
			// 创建当帧直接注入投影后定义，禁止目标样条再发起异步 Worker 重拟合造成预览/提交差异。
			return new SketchSplineObject({
				referencePlane: input.targetSketch.referencePlane,
				startPoint,
				endPoint,
				controlPoints: interiorControlPoints,
				curvePoints: input.geometryPayload.curvePointWorlds.map(toVector3),
				curveMetadata: input.geometryPayload.curveMetadata,
				...(input.geometryPayload.curveEvaluationDefinition
					? {curveEvaluationDefinition: input.geometryPayload.curveEvaluationDefinition}
					: {}),
			});
		} catch (error) {
			injectedEndpointParentScope.restore();
			throw error;
		}
	}
}

/**
 * 创建引用链路使用的控制点。
 *
 * @param position 世界坐标。
 * @param targetSketch 目标草图。
 * @returns 控制点对象。
 */
function createControlPoint(
	position: WorldCoordinate,
	targetSketch: SketchGroup,
): SketchPointObject {
	return new SketchPointObject({
		position: toVector3(position),
		pointKind: SketchPointKind.ControlPoint,
		material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
		referencePlane: targetSketch.referencePlane,
	});
}

/**
 * 将世界坐标元组转为向量。
 *
 * @param position 世界坐标元组。
 * @returns 三维向量。
 */
function toVector3(position: WorldCoordinate): THREE.Vector3 {
	return new THREE.Vector3(...position);
}
